import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track pending requests to prevent duplicates
const pendingRequests = new Map();

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Prevent duplicate requests
    const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.data || {})}`;

    if (pendingRequests.has(requestKey)) {
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
      console.log('[API] Duplicate request blocked:', requestKey);
    } else {
      pendingRequests.set(requestKey, true);
      config.metadata = { requestKey };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`[API] Response: ${response.status} ${response.config.url}`, response.data);
    // Clear pending request
    if (response.config.metadata?.requestKey) {
      pendingRequests.delete(response.config.metadata.requestKey);
    }
    return response;
  },
  (error) => {
    console.error(`[API] Error: ${error.response?.status} ${error.config?.url}`, error.response?.data || error.message);
    // Clear pending request
    if (error.config?.metadata?.requestKey) {
      pendingRequests.delete(error.config.metadata.requestKey);
    }

    // Handle auth errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
};

// Tasks API
export const tasksAPI = {
  getAll: () => api.get('/tasks'),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (taskData) => api.post('/tasks', taskData),
  update: (id, taskData) => api.put(`/tasks/${id}`, taskData),
  delete: (id) => api.delete(`/tasks/${id}`),
  updateStatus: (id, status) => api.patch(`/tasks/${id}/status`, { status }),
  assignUser: (id, userId) => api.patch(`/tasks/${id}/assign`, { assignedTo: userId }),
  approve: (id, body) => api.post(`/tasks/${id}/approve`, body || {}),
  requestChanges: (id, body) => api.post(`/tasks/${id}/request-changes`, body || {}),
  addReview: (id, body) => api.post(`/tasks/${id}/reviews`, body || {}),
  assignProject: (id, projectId) => api.patch(`/tasks/${id}/project`, { projectId }),
  getTrash: () => api.get('/tasks/deleted/trash'),
  restore: (id) => api.put(`/tasks/${id}/restore`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  getProfile: () => api.get('/users/profile'),
  changePassword: (data) => api.put('/users/change-password', data),
  uploadProfilePicture: (formData) => api.post('/users/profile-picture', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteProfilePicture: () => api.delete('/users/profile-picture'),
  // Leave requests
  getLeaves: () => api.get('/users/leaves'),
  createLeave: (data) => api.post('/users/leaves', data),
  cancelLeave: (id) => api.delete(`/users/leaves/${id}`),
  getPendingApprovals: () => api.get('/users/leaves/pending-approvals'),
  getAllLeaves: () => api.get('/users/leaves/all'),
  approveLeave: (id, comment) => api.put(`/users/leaves/${id}/approve`, { comment }),
  rejectLeave: (id, comment) => api.put(`/users/leaves/${id}/reject`, { comment }),
  getMyProjects: () => api.get('/users/projects'),
};

// Admin API
export const adminAPI = {
  getOrgTree: () => api.get('/admin/org-tree'),
  getTasks: () => api.get('/admin/tasks'),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  updateUserManager: (userId, managerId) => api.put(`/admin/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/admin/users/${userId}/department`, { department }),
  assignUserProjects: (userId, taskIds) => api.put(`/admin/users/${userId}/projects`, { taskIds }),
  // Users
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Projects
  getProjects: () => api.get('/admin/projects'),
  createProject: (data) => api.post('/admin/projects', data),
  updateProject: (id, data) => api.put(`/admin/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/admin/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/admin/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/admin/projects/${projectId}/members/${userId}`),
};

// Manager API
export const managerAPI = {
  getMyTeam: () => api.get('/manager/my-team'),
  getMyProjects: () => api.get('/manager/my-projects'),
  createProject: (data) => api.post('/manager/projects', data),
  updateProject: (id, data) => api.put(`/manager/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/manager/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/manager/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/manager/projects/${projectId}/members/${userId}`),
  // Users (Subtree)
  updateUserRole: (userId, role) => api.put(`/manager/users/${userId}/role`, { role }),
  updateUserManager: (userId, managerId) => api.put(`/manager/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/manager/users/${userId}/department`, { department }),
};

// HR API (mirrors admin for people management, but cannot manage admin profiles)
export const hrAPI = {
  getOrgTree: () => api.get('/hr/org-tree'),
  getTasks: () => api.get('/hr/tasks'),
  updateUserRole: (userId, role) => api.put(`/hr/users/${userId}/role`, { role }),
  updateUserManager: (userId, managerId) => api.put(`/hr/users/${userId}/manager`, { managerId }),
  updateUserDepartment: (userId, department) => api.put(`/hr/users/${userId}/department`, { department }),
  // Users
  createUser: (data) => api.post('/hr/users', data),
  deleteUser: (userId) => api.delete(`/hr/users/${userId}`),
  // Projects
  getProjects: () => api.get('/hr/projects'),
  createProject: (data) => api.post('/hr/projects', data),
  updateProject: (id, data) => api.put(`/hr/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/hr/projects/${id}`),
  addProjectMember: (projectId, userId) => api.put(`/hr/projects/${projectId}/members`, { userId }),
  removeProjectMember: (projectId, userId) => api.delete(`/hr/projects/${projectId}/members/${userId}`),
};

export default api;
